import React from 'react';
import { DeepPageWithShell } from './DeepPage';
import { usePageMeta } from '../../lib/useMeta';
import { serviceSchema } from '../../lib/schema';

const PATH = '/adult-karate';

const DATA = {
  breadcrumbs: [
    { label: 'Home', href: '/' },
    { label: 'Programs', href: '/#programs' },
    { label: 'Adult Karate' },
  ],
  eyebrow: 'Adult Program',
  h1: 'Adult Karate in Colorado Springs',
  tagline: 'Kempo Karate · Practical Self-Defense · Fitness',
  intro:
    "Universal Kempo Karate\u2019s adult program is designed for working adults, parents, and lifelong beginners who want to train the way karate was meant to be trained \u2014 for real-world self-defense, serious fitness, and a sharper mind. Our Revolution location in northeast Colorado Springs welcomes adults of every age and background.",
  heroImage: 'https://customer-assets.emergentagent.com/job_kempo-patch/artifacts/5m2giynl_4.jpeg',
  heroImageAlt: 'Adult karate class at Universal Kempo Karate in Colorado Springs',
  callouts: [
    { title: 'Beginners Welcome', body: 'No experience required. Our instructors tailor each class to your level.' },
    { title: 'Family-Owned Since 1980', body: 'A veteran-founded Colorado Springs karate school with 40+ years in the community.' },
    { title: 'Two Free Classes', body: 'Try your first two adult karate classes on us. No pressure, no commitment.' },
  ],
  featureList: [
    'Real-world self-defense (not sport-only)',
    'Full-body conditioning & cardio',
    'Beginner-friendly, zero contact pressure',
    'Family-run, judgment-free environment',
    'Progressive Kempo belt curriculum',
    'Train alongside instructors who actually care',
  ],
  sections: [
    {
      eyebrow: 'What You\u2019ll Learn',
      heading: 'Real Kempo, taught the right way.',
      sub: 'Our adult curriculum pulls from the full Kempo system \u2014 striking, blocks, flow drills, grappling fundamentals, and pressure-tested self-defense scenarios.',
      body: [
        'Each class blends technical practice (stances, strikes, footwork, blocks) with practical application. Expect partner drills at a controlled, respectful intensity \u2014 the kind of training that builds real reflexes without turning class into a fight gym.',
        'As you progress, you\u2019ll move through the Kempo belt curriculum. Ranks are earned through consistent training and technique testing \u2014 never given. Many of our longest-standing adult students arrived as complete beginners.',
      ],
      bullets: [
        'Striking: hands, elbows, knees, kicks',
        'Blocks and defensive flow drills',
        'Grappling fundamentals and joint locks',
        'Scenario-based self-defense',
        'Kata and traditional form study',
      ],
    },
    {
      eyebrow: 'What to Expect',
      heading: 'Your first adult karate class.',
      body: [
        'Walk in 10 minutes early. Wear comfortable athletic clothes \u2014 we\u2019ll loan you a uniform at your first class if you\u2019d like to try one. We train barefoot on padded mats.',
        'Class opens with a warm-up (stretching, calisthenics), moves into technique drills, and finishes with partner work or forms. Our instructors introduce themselves, learn your name, and keep an eye on anything you need \u2014 injuries, time pressure, or just a nervous first class.',
        'You will not be thrown into contact sparring on day one. Ever. Build a foundation first, build confidence second.',
      ],
    },
    {
      eyebrow: 'Who It\u2019s For',
      heading: 'Built for adults who train for life, not for a tournament.',
      body: [
        'Universal Kempo Karate\u2019s adult program fits parents who want to set an example, professionals who need a smarter way to decompress, veterans looking for structure, and lifelong beginners who have been \u201Cmeaning to try martial arts\u201D for years.',
        'If you are coming back after a long break, you will find a patient room full of people doing the same thing.',
      ],
    },
  ],
  ctaCopy: 'Book My Adult Trial Class',
  source: 'page-adult-karate',
  bottomNote:
    'Verified by owner: Program description reflects the school\u2019s current adult curriculum. Specific class times and pricing are quoted during your first visit.',
};

const AdultKaratePage = () => {
  usePageMeta({
    title: 'Adult Karate in Colorado Springs, CO | Universal Kempo Karate',
    description:
      'Adult karate classes in Colorado Springs since 1980. Kempo karate, practical self-defense, and fitness for every age and background. Two free trial classes. No experience required.',
    path: PATH,
    breadcrumbs: DATA.breadcrumbs,
    schemas: [
      {
        id: 'adult-karate-service-jsonld',
        data: serviceSchema({
          name: 'Adult Karate',
          description: 'Adult Kempo karate, self-defense, and fitness classes for all levels.',
          slug: PATH,
          serviceType: 'Martial Arts Instruction',
        }),
      },
    ],
  });
  return <DeepPageWithShell data={DATA} />;
};

export default AdultKaratePage;
